import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Copy, AlertCircle, CheckCircle2 } from 'lucide-react';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta } from '../seo/Meta.jsx';
import { verifySignup } from '../lib/api.js';

// Verify screen  -  consumes /signup/verify?token=... once. Shows the
// freshly minted API key ONCE per ADR 0051. Customer copies it now or
// regenerates via the customer portal.
export default function Verify() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState({ loading: true });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, err: 'Missing token in URL' });
      return;
    }
    verifySignup(token)
      .then((res) => setState({ loading: false, data: res }))
      .catch((e) => setState({ loading: false, err: e?.body?.error?.message || e.message }));
  }, [token]);

  return (
    <>
      <Meta
        title="Verify your email"
        description="Verifying your tender-api signup. Your API key is shown once on this page."
        path="/signup/verify"
        noindex
      />
      <Header />
      <main className="container" style={{ paddingTop: 'var(--space-10)', paddingBottom: 'var(--space-12)', maxWidth: 640 }}>
        <h1 style={{ fontSize: 'var(--fs-36)', fontWeight: 700, letterSpacing: 'var(--tracking-tight)', margin: 0 }}>
          Verifying your email…
        </h1>

        {state.loading && (
          <div style={{ marginTop: 32, color: 'var(--fg-3)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Loader2 size={16} className="spin" /> verifying token
          </div>
        )}

        {state.err && (
          <div
            style={{
              marginTop: 32,
              padding: 24,
              border: '1px solid var(--danger)',
              borderRadius: 'var(--r-3)',
              background: '#fef2f2',
              display: 'flex',
              gap: 16,
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle size={20} color="var(--danger)" />
            <div>
              <h3 style={{ margin: 0, color: 'var(--danger)', fontWeight: 700 }}>
                Verification failed
              </h3>
              <p style={{ margin: '6px 0 0', color: 'var(--fg-1)' }}>{state.err}</p>
              <Link to="/signup" style={{ color: 'var(--accent)', fontWeight: 600, marginTop: 12, display: 'inline-block' }}>
                Start over →
              </Link>
            </div>
          </div>
        )}

        {state.data && (
          <>
            <div
              style={{
                marginTop: 32,
                padding: 24,
                background: 'var(--accent-soft)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--r-3)',
                display: 'flex',
                gap: 16,
                alignItems: 'flex-start',
              }}
            >
              <CheckCircle2 size={22} color="var(--accent)" />
              <div>
                <h3 style={{ margin: 0, fontSize: 'var(--fs-22)', fontWeight: 700, color: 'var(--fg-0)' }}>
                  Workspace activated
                </h3>
                <p style={{ margin: '6px 0 0', color: 'var(--fg-2)' }}>{state.data.message}</p>
              </div>
            </div>

            {state.data.api_key ? (
              <div style={{ marginTop: 32 }}>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--fs-12)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-wider)',
                    color: 'var(--accent)',
                    fontWeight: 600,
                  }}
                >
                  Your API key  -  shown once
                </div>
                <div
                  style={{
                    marginTop: 8,
                    padding: 16,
                    background: 'var(--fg-0)',
                    color: '#a5b4fc',
                    borderRadius: 'var(--r-2)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--fs-14)',
                    wordBreak: 'break-all',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <span style={{ flex: 1 }}>{state.data.api_key}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(state.data.api_key);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    style={{
                      background: copied ? 'var(--success)' : 'var(--accent)',
                      color: '#fff',
                      border: 0,
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontFamily: 'inherit',
                      fontSize: 'var(--fs-12)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Copy size={14} /> {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p
                  style={{
                    fontSize: 'var(--fs-13)',
                    color: 'var(--danger)',
                    marginTop: 8,
                    fontWeight: 500,
                  }}
                >
                  Save it now in 1Password / your vault. We will not show it again. If lost,
                  regenerate via the customer portal  -  there's no recovery path.
                </p>
              </div>
            ) : (
              <div style={{ marginTop: 32, color: 'var(--fg-2)' }}>
                Workspace verified, but no API key returned (likely a server-side mint
                failure). Contact <a href="mailto:support@tender-api.com" style={{ color: 'var(--accent)' }}>support@tender-api.com</a>.
              </div>
            )}

            {state.data.checkout_url ? (
              <a
                href={state.data.checkout_url}
                style={{
                  marginTop: 24,
                  display: 'inline-block',
                  padding: '14px 24px',
                  background: 'var(--accent)',
                  color: 'var(--accent-fg)',
                  borderRadius: 'var(--r-3)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-16)',
                }}
              >
                Add a payment method →
              </a>
            ) : (
              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: 'var(--bg-2)',
                  borderRadius: 'var(--r-2)',
                  fontSize: 'var(--fs-13)',
                  color: 'var(--fg-3)',
                }}
              >
                Stripe billing not configured on this deployment yet. Hand-deliver the API
                key + we'll wire Checkout when STRIPE_SECRET_KEY is set on the API.
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
